import OrderPreviewReconnect from './OrderPreviewReconnect';
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { counterCultureProfile } from "../lib/counterCultureBrand";
import { useOrderStartup } from "./OrderStartupBoundary";

import {brandVerificationFailure} from "../lib/brandVerificationFailure";

const BrandContext = createContext(null);
const BrandVerificationContext = createContext(null);
const EXPECTED = process.env.REACT_APP_BRAND_KEY || "rolls_and_brew";
export const useOptionalBrandVerification = () => useContext(BrandVerificationContext);

export function useBrandIdentity() {
  const profile = useContext(BrandContext);
  if (!profile) throw new Error("Brand identity is unavailable outside BrandRuntimeGate");
  return profile;
}

export default function BrandRuntimeGate({ children }) {
  const startup = useOrderStartup();
  const report = startup?.report;
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ status: "loading" });

  useEffect(() => {
    let active = true;
    setResult({ status: "loading" });
    api.get("/brand/current", { timeout: 8000 }).then(({ data }) => {
      if (!active) return;
      if (data?.brand_key === EXPECTED && typeof data.display_name === "string" && data.display_name) {
        setResult({ status: "ready", profile: counterCultureProfile(data), verifiedAt: Date.now() });
      } else {
        setResult({ status: "mismatch" });
      }
    }).catch(error => { if (active) setResult({ status: brandVerificationFailure(error) }); });
    return () => { active = false; };
  }, [attempt]);

  useEffect(() => { report?.("brand", result.status); }, [report, result.status]);

  if (result.status === "ready") {
    return <BrandVerificationContext.Provider value={result}>
      <BrandContext.Provider value={result.profile}>{children}</BrandContext.Provider>
    </BrandVerificationContext.Provider>;
  }
  if (result.status === "loading" && startup?.visible) return null;
  return (
    <main role="status" aria-live="polite" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, color: "var(--cc-text, #3C2415)", background: "var(--cc-bg, #F9F5EB)" }}>
      <section style={{ maxWidth: 460, padding: 28, border: "1px solid var(--cc-border, #E8DDC8)", borderRadius: 18, background: "white" }}>
        <div style={{ fontWeight: 800, letterSpacing: ".12em", fontSize: 12 }}>CULINEXOS</div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, margin: "14px 0 10px" }}>
          {result.status === "loading" ? "Opening workspace" : result.status === "mismatch" ? "Workspace configuration needs attention" : result.status === "preview-expired" ? (/^\/order\/?$/i.test(window.location.pathname)?"Reconnect CC Order":"Open a fresh Test page") : "Workspace temporarily unavailable"}
        </h1>
        <p style={{ color: "var(--cc-muted, #6B6B6B)", lineHeight: 1.5 }}>
          {result.status === "loading" ? "Verifying your restaurant…" : result.status === "mismatch"
            ? "This application is connected to a different restaurant. Ask your administrator to check its configuration."
            : result.status === "preview-expired" ? (/^\/order\/?$/i.test(window.location.pathname)?"This page needs a valid Test outlet connection. Reconnect to your selected Test workspace below. Your saved orders and account are preserved.":"This Test/Demo link has expired or is no longer valid. In the staff app, select your outlet and Test/Demo mode, then open a fresh PWA test page. Your saved orders and account are preserved.") : "We could not verify this restaurant. Check your connection and try again."}
        </p>
        {result.status === "preview-expired" && (/^\/order\/?$/i.test(window.location.pathname)?<OrderPreviewReconnect/>:<a href="/" data-testid="open-staff-for-preview" style={{display:"inline-flex",alignItems:"center",minHeight:44,padding:"0 20px",borderRadius:10,background:"var(--cc-action, #126d68)",color:"var(--cc-on-action, white)",textDecoration:"none",fontWeight:600}}>Open staff app</a>)}
        {result.status === "error" && <button type="button" onClick={() => setAttempt((n) => n + 1)} style={{ minHeight: 44, background: "var(--cc-action, #9A3E00)", color: "var(--cc-on-action, white)", border: 0, borderRadius: 10, padding: "0 20px", cursor: "pointer" }}>Try again</button>}
      </section>
    </main>
  );
}
